import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, Plus, ArrowLeft, Sparkles, Users, GraduationCap, Flag, ShieldCheck } from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import SafetyReportDialog from '../components/SafetyReportDialog';

const ROOM_OPTIONS = [
  { value: 'general', label: 'General Circle' },
  { value: 'interview-prep', label: 'Interview Prep' },
  { value: 'industry', label: 'Industry Room' },
  { value: 'mentors', label: 'Mentors' },
  { value: 'offer-talk', label: 'Offers & Salary' },
];

const AI_PROMPTS = [
  'Ask for mock interview questions tailored to this role.',
  'Ask someone in the industry what hiring managers screen for.',
  'Ask a mentor to review your strongest answer.',
  'Ask the group what salary, tools, and red flags to expect.',
];

export default function CommunityHub({ user }) {
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [room, setRoom] = useState('general');
  const [industryFilter, setIndustryFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [safetyReport, setSafetyReport] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadPosts();
    loadMessages();
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, [room, industryFilter, jobFilter]);

  const loadPosts = async () => {
    try {
      const res = await api.get('/forum/posts');
      setPosts(res.data);
    } catch (error) {
      toast.error('Failed to load posts');
    }
  };

  const loadMessages = async () => {
    try {
      const params = new URLSearchParams();
      params.set('room', room);
      if (industryFilter.trim()) params.set('industry', industryFilter.trim());
      if (jobFilter.trim()) params.set('job_target', jobFilter.trim());
      const res = await api.get(`/chat/messages?${params.toString()}`);
      setMessages(res.data);
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.target);
    const data = {
      title: formData.get('title'),
      content: formData.get('content'),
      category: formData.get('category'),
    };

    try {
      await api.post('/forum/posts', data);
      toast.success('Post created successfully');
      setShowCreatePost(false);
      loadPosts();
      e.target.reset();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await api.post('/chat/messages', {
        message: newMessage,
        room,
        industry: industryFilter,
        job_target: jobFilter,
      });
      setNewMessage('');
      loadMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const handleBack = () => {
    const role = localStorage.getItem('role');
    navigate(role === 'company' ? '/company/dashboard' : '/candidate/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack} data-testid="back-btn">
              <ArrowLeft size={16} />
            </Button>
            <img src="/logo.svg" alt="The Interview Logo" className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-heading font-bold">Community</h1>
              <p className="text-sm text-muted-foreground">Find your people by role, industry, mentor lane, and interview goal.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/marketplace')} data-testid="community-marketplace-btn">
              Marketplace
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/mentorship')} data-testid="community-mentors-btn">
              Mentors
            </Button>
            <div className="text-sm text-muted-foreground">{user?.full_name}</div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="mb-6 flex items-start gap-3 border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950">
          <ShieldCheck className="mt-0.5 shrink-0" size={20} />
          <div><p className="font-semibold">Community Safety is active</p><p className="mt-1">Automated checks screen written content for bullying, harassment, threats, and discrimination. Use the flag beside any post or message to request confidential human review.</p></div>
        </div>
        <div className="grid lg:grid-cols-[1fr_320px] gap-6 mb-8">
          <div className="bg-white border p-6">
            <div className="flex items-center gap-3 mb-4">
              <Users className="text-blue-600" />
              <div>
                <h2 className="text-xl font-heading font-semibold">Career Circles</h2>
                <p className="text-sm text-muted-foreground">Chat like a close campus group, but organized around the job you want.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="community-room">Room</Label>
                <select
                  id="community-room"
                  value={room}
                  onChange={(e) => setRoom(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  data-testid="community-room-select"
                >
                  {ROOM_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="industry-filter">Industry</Label>
                <Input
                  id="industry-filter"
                  value={industryFilter}
                  onChange={(e) => setIndustryFilter(e.target.value)}
                  placeholder="Healthcare, tech, finance..."
                  data-testid="industry-filter-input"
                />
              </div>
              <div>
                <Label htmlFor="job-filter">Job target</Label>
                <Input
                  id="job-filter"
                  value={jobFilter}
                  onChange={(e) => setJobFilter(e.target.value)}
                  placeholder="Nurse, analyst, manager..."
                  data-testid="job-filter-input"
                />
              </div>
            </div>
          </div>

          <div className="bg-slate-900 text-white border border-slate-800 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="text-cyan-300" />
              <h2 className="font-heading font-semibold">AI Conversation Coach</h2>
            </div>
            <div className="space-y-2">
              {AI_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  onClick={() => setNewMessage(prompt)}
                  className="w-full text-left text-sm bg-white/10 hover:bg-white/15 rounded px-3 py-2 transition-colors"
                  data-testid="ai-chat-prompt"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        </div>

        <Tabs defaultValue="forum" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-8">
            <TabsTrigger value="forum" data-testid="forum-tab">Forum</TabsTrigger>
            <TabsTrigger value="chat" data-testid="chat-tab">Live Chat</TabsTrigger>
          </TabsList>

          <TabsContent value="forum" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-heading font-semibold">Discussion Forum</h2>
              <Button onClick={() => setShowCreatePost(true)} data-testid="create-post-btn">
                <Plus size={16} className="mr-2" />
                New Post
              </Button>
            </div>

            <div className="space-y-4" data-testid="posts-list">
              {posts.length === 0 ? (
                <p className="text-muted-foreground text-center py-12">No posts yet. Start the conversation!</p>
              ) : (
                posts.map((post) => (
                  <div key={post.id} className="bg-white border p-6 card-hover" data-testid={`post-${post.id}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <MessageSquare className="text-blue-600" size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold">{post.title}</h3>
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded">{post.category}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{post.content}</p>
                        <div className="text-xs text-muted-foreground">
                          Posted by {post.author_name} • {new Date(post.created_at).toLocaleDateString()}
                        </div>
                        {post.author_id !== user?.id && (
                          <Button type="button" size="sm" variant="ghost" className="mt-2 h-8 text-xs text-muted-foreground" onClick={() => setSafetyReport({ subject: { id: post.author_id, name: post.author_name }, sourceType: 'forum_post', sourceId: post.id })}>
                            <Flag size={13} className="mr-1" /> Report
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="chat">
            <div className="bg-white border rounded-lg">
              <div className="p-4 border-b">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h2 className="font-heading font-semibold">Live Career Chat</h2>
                    <p className="text-sm text-muted-foreground">Room: {ROOM_OPTIONS.find(option => option.value === room)?.label}</p>
                  </div>
                  <Badge variant="outline" className="gap-1">
                    <GraduationCap size={14} />
                    Mentor-ready
                  </Badge>
                </div>
              </div>

              <div className="h-96 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                {messages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`flex gap-3 ${msg.user_id === user?.id ? 'flex-row-reverse' : ''}`}
                    data-testid={`message-${msg.id}`}
                  >
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-semibold">
                      {msg.user_name.charAt(0).toUpperCase()}
                    </div>
                    <div className={`max-w-xs ${msg.user_id === user?.id ? 'bg-blue-600 text-white' : 'bg-gray-100'} rounded-lg p-3`}>
                      <p className="text-xs font-semibold mb-1">{msg.user_name}</p>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {msg.room && <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/10">{ROOM_OPTIONS.find(option => option.value === msg.room)?.label || msg.room}</span>}
                        {msg.industry && <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/10">{msg.industry}</span>}
                        {msg.job_target && <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/10">{msg.job_target}</span>}
                      </div>
                      <p className="text-sm">{msg.message}</p>
                      {msg.user_id !== user?.id && (
                        <button type="button" className="mt-2 flex items-center gap-1 text-[11px] opacity-70 hover:opacity-100" onClick={() => setSafetyReport({ subject: { id: msg.user_id, name: msg.user_name }, sourceType: 'community_chat', sourceId: msg.id })}>
                          <Flag size={11} /> Report
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {messages.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-16">
                    Start this circle with a question about the role, company, interview, or industry.
                  </p>
                )}
              </div>

              <div className="p-4 border-t flex gap-2">
                <Input 
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  data-testid="chat-input"
                />
                <Button onClick={handleSendMessage} data-testid="send-message-btn">
                  <Send size={16} />
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showCreatePost} onOpenChange={setShowCreatePost}>
        <DialogContent data-testid="create-post-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Post</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreatePost} className="space-y-4">
            <div>
              <Label htmlFor="post-title">Title</Label>
              <Input id="post-title" name="title" required data-testid="post-title-input" />
            </div>
            <div>
              <Label htmlFor="post-category">Category</Label>
              <select 
                id="post-category" 
                name="category" 
                required 
                className="w-full border rounded px-3 py-2"
                data-testid="post-category-select"
              >
                <option value="general">General</option>
                <option value="interview-tips">Interview Tips</option>
                <option value="career-advice">Career Advice</option>
                <option value="success-stories">Success Stories</option>
              </select>
            </div>
            <div>
              <Label htmlFor="post-content">Content</Label>
              <Textarea id="post-content" name="content" required data-testid="post-content-input" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="submit-post-btn">
              {loading ? 'Posting...' : 'Create Post'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <SafetyReportDialog
        open={Boolean(safetyReport)}
        onOpenChange={(open) => { if (!open) setSafetyReport(null); }}
        subject={safetyReport?.subject}
        sourceType={safetyReport?.sourceType || 'community'}
        sourceId={safetyReport?.sourceId || ''}
      />
      <Footer />
    </div>
  );
}
